import {
  appFormUtil,
  gqlCacheUtil
} from 'modules/common';
import React, { useEffect } from 'react';

import {
  useSaveClientReportLinkMutation,
  SaveClientReportLink_Input,
  useClientReportLinkFormQuery,
  SaveClientReportLinkMutation,
  ClientReportDetailSectionQuery,
  ClientReportDetailSectionDocument
} from 'generated/graphql';

import _ from 'lodash';

export type IFormState = {
  clientReportId: string;
  id: string;
  notesForClient: string;
  url: string;
};

const initialState: IFormState = {
  clientReportId: '',
  id: '',
  notesForClient: '',
  url: ''
};

export function useFormState(id: string, clientReportId: string) {
  const { formState, dispatch, genFieldProps } = appFormUtil.useFormState({
    initialState,
    key2FieldConfig: {
      clientReportId: {
        label: 'Client report id',
        type: 'text'
      },
      id: {
        label: 'Id',
        type: 'text'
      },
      notesForClient: {
        label: 'Name',
        type: 'text'
      },
      url: {
        label: 'Url',
        type: 'text'
      }
    }
  });

  useEffect(() => {
    if (formState.clientReportId === '' && clientReportId !== '') {
      dispatch({
        type: 'UPDATE',
        payload: {
          property: 'clientReportId',
          value: clientReportId
        }
      });
    }
    return () => {};
  }, [
    clientReportId,
    dispatch,
    formState.clientReportId
  ]);


  const { data, loading, called } = useQuery(id);

  useEffect(() => {
    if (formState.id === '' && data.id !== '' && !loading && called) {
      dispatch({
        type: 'BATCH_UPDATE',
        payload: {
          ...formState,
          ...data
        }
      });
    }
    return () => {};
  }, [
    called,
    data,
    dispatch,
    formState,
    loading
  ]);

  return React.useMemo(
    () => {

      return {
        formState,
        dispatch,
        genFieldProps
      };
    },
    [
      dispatch,
      formState,
      genFieldProps
    ]
  );
}


export interface IData extends Omit<IFormState, 'clientReportId'> {}

export function useQuery(id: string) {
  const tuple = useClientReportLinkFormQuery({
    variables: {
      id
    },
    skip: id === ''
  });

  return React.useMemo(
    () => {
      const data: IData = {
        id: tuple.data?.clientReportLink?.id ?? '',
        notesForClient: tuple.data?.clientReportLink?.notesForClient ?? '',
        url: tuple.data?.clientReportLink?.url ?? ''
      };

      return {
        ...tuple,
        data
      };
    },
    [tuple]
  );
}

export function useSaveClientReportLink() {
  const [
    fnMutate,
    tuple
  ] = useSaveClientReportLinkMutation({
    refetchQueries: []
  });
  const fnWrappedMutate = React.useCallback(
    async (input: SaveClientReportLink_Input) => {
      await fnMutate({
        variables: {
          input
        },

        updateQueries: {
          ...gqlCacheUtil.genUpdateQueryMap<ClientReportDetailSectionQuery, SaveClientReportLinkMutation>(ClientReportDetailSectionDocument, (prevState, { mutationResult }) => {
            if (!input || !prevState.clientReport?.id || !mutationResult.data?.saveClientReportLink) {
              return prevState;
            }

            let updateLinks;
            if (!input.id) {
              updateLinks = [
                ...prevState.clientReport?.links,
                mutationResult.data.saveClientReportLink
              ];

            } else {
              updateLinks = prevState.clientReport?.links.map((node) => {
                const found = node.id === input.id;
                if (found) {
                  return {
                    ...node,
                    ...mutationResult.data?.saveClientReportLink
                  };
                }
                return node;
              });
            }
            return {
              ...prevState,
              clientReport: {
                ...prevState.clientReport,
                links: _.compact(updateLinks)
              }
            };

          })
        }
      });
    },
    [fnMutate]
  );

  return React.useMemo(
    () => [
      fnWrappedMutate,
      {
        ...tuple,
        data: null
      }
    ] as const,
    [
      fnWrappedMutate,
      tuple
    ]
  );
}
